import React, {useEffect, useState} from 'react';
import {RiseInterstitialProps} from 'screens/InsomniaSurvey/components/RiseInterstitialTemplate/RiseInterstitialBase';
import RiseSurveyInterstitialControls from 'screens/InsomniaSurvey/components/RiseSurveyControls/RiseSurveyInterstitialControls';
import styles from './ISISnapshotInterstitial.module.css';
import common from 'common.module.css';
import SegmentedScale from 'screens/InsomniaSurvey/components/SegmentedScale/SegmentedScale';
import {getISIScore} from 'screens/InsomniaSurvey/utils/isiScoring';
import Analytics from 'utils/Analytics';

enum SleepQuality {
  Critical = 0,
  Poor = 1,
  AtRisk = 2,
  Healthy = 3,
}

const Bullet = () => (
  <svg
    className={styles.bulletIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0Z"
      fill="#E8DEFF"
    />
    <path
      d="M15.127 7.46875L9.10986 13.4858L4.97311 9.34909"
      stroke="#382365"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ISISleepQualityInterstitial: React.FC<RiseInterstitialProps> = () => {
  const sleepQualityIndex = (score: number): number => {
    switch (true) {
      case score <= 7:
        return SleepQuality.Healthy;
      case score >= 8 && score <= 14:
        return SleepQuality.AtRisk;
      case score >= 15 && score <= 21:
        return SleepQuality.Poor;
      default:
        return SleepQuality.Critical;
    }
  };

  const titleColors = ['var(--color-red1)', 'var(--color-orange1)', 'var(--color-orange1)', 'var(--color-green1)'];
  const titleTexts = [
    'This could be severe insomnia.',
    'It could be insomnia.',
    'It might be insomnia.',
    'Your sleep quality is in a healthy range, with minor challenges',
  ];
  const descriptionTexts = [
    'Your sleep quality indicates struggles that might be more serious than you think. ',
    'Your sleep quality indicates struggles that aren’t just about willpower.',
    'Your sleep quality indicates struggles that could be more than just bad habits. ',
    'Your sleep quality indicates a healthy range, with minor challenges',
  ];
  const bulletItems = [
    [
      'Significant sleep disruptions are likely taking a toll on your energy and health',
      'Severe insomnia often goes unrecognized, but it can deeply impact your life',
      'RISE Sleep Experts specialize in helping people like you regain control and healthy sleep',
    ],
    [
      'Severe sleep disruptions are impacting your energy, mood, and health',
      'Many people with similar challenges discover they’re facing insomnia',
      'RISE Sleep Experts can help you uncover what’s holding you back and show you how to fix it',
    ],
    [
      'Moderate sleep issues may already be affecting your energy and focus—it’s frustrating, but common',
      'These could be early signs of insomnia, and addressing them now can make a big difference',
      'RISE Sleep Experts can help you identify and fix these patterns before they worsen',
    ],
    [
      'Your sleep quality is in a healthy range, with minor challenges',
      "Based on your score, we'll personalize your path to maintain and improve your sleep",
    ],
  ];

  const sleepQualityLabels = ['Critical', 'Poor', 'At risk', 'Healthy'];
  const sleepQualityGradients = [
    ['#FF4A8B', '#FF3963', '#721CFF'],
    ['#FFB800', '#FD8206', '#721CFF'],
    ['#FFD600', '#FD8206', '#721CFF'],
    ['#00B38B', '#721CFF'],
  ];
  const sleepQualityGradientLocations = [
    [0.0, 0.5076, 1.4086],
    [0.0, 0.4802, 1.3721],
    [0.0, 0.4802, 1.3721],
    [0.17, 1],
  ];

  const [isiScore, setIsiScore] = useState(getISIScore());

  const index = sleepQualityIndex(getISIScore());

  useEffect(() => {
    Analytics.identify({web_sleep_quality: isiScore.toString()});
  }, [isiScore]);

  useEffect(() => {
    setIsiScore(getISIScore());
  }, []);

  // Results screen
  return (
    <div className={`${styles.container} ${styles.fadeIn}`}>
      <div className={common.heading1}>My sleep quality</div>

      <div className={styles.insomniaContainer}>
        <SegmentedScale
          options={['Critical', 'Poor', 'At risk', 'Healthy']}
          selectedOption={sleepQualityLabels[index]}
          selectedGradientColors={sleepQualityGradients[index]}
          selectedGradientLocations={sleepQualityGradientLocations[index]}
          score={getISIScore()}
        />

        <div className={styles.insomniaContent}>
          <div className={common.heading3} style={{color: titleColors[index], fontWeight: 700}}>
            {titleTexts[index]}
          </div>
          <div className={common.heading3}>{descriptionTexts[index]}</div>

          <div className={styles.bulletsContainer} style={{marginTop: 24}}>
            {bulletItems[index].map((item, index) => (
              <div key={index} className={styles.bulletItem}>
                <Bullet />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      <RiseSurveyInterstitialControls continueButtonTitle="Next" />
    </div>
  );
};

export default ISISleepQualityInterstitial;
